/**
 * Interactive Path - handles navigation through branching learning paths
 *
 * Structure expected:
 * <div class="interactive-path" data-start-id="node1">
 *   <div class="pathnode" data-id="node1" data-nextid="node2" data-type="">
 *     <div class="pathnode-content">
 *       <div class="pathtitle">...</div>
 *       <div class="pathbody">...</div>
 *     </div>
 *     <div class="pathnode-nav">
 *       <div class="pathnode-nav-left"><button class="pathnode-restart">Start Over</button></div>
 *       <div class="pathnode-nav-right"><button class="pathnode-next">Next</button></div>
 *     </div>
 *   </div>
 *   <div class="pathnode" data-id="node2" data-type="question">
 *     <div class="pathnode-content">
 *       ...
 *       <div class="pathoptions">
 *         <button class="pathoption" data-nextid="node3">Option 1</button>
 *       </div>
 *     </div>
 *   </div>
 *   <div class="pathnode" data-id="node5" data-type="terminal">
 *     <div class="pathnode-content">...</div>
 *     <div class="pathnode-nav">
 *       <div class="pathnode-nav-left"><button class="pathnode-restart">Start Over</button></div>
 *       <div class="pathnode-nav-right"></div>
 *     </div>
 *   </div>
 * </div>
 */

import Player from '@vimeo/player'

class InteractivePath {
  constructor(container) {
    this.container = container;
    this.$container = $(container);
    this.startId = this.$container.data('start-id');
    this.currentNodeId = this.startId;
    this.history = []; // Track navigation history for potential "back" feature
    this.isTransitioning = false; // Guard against rapid clicks during animation

    this.init();
  }

  init() {
    // Validate start node exists before doing any setup
    if (this.$container.find(`.pathnode[data-id="${this.startId}"]`).length === 0) {
      console.error(`InteractivePath: start node "${this.startId}" not found`);
      return;
    }

    // Calculate and lock the height before hiding nodes
    this.setFixedHeight();
    this.bindResizeHandler();

    // Ensure only the start node is visible
    this.$container.find('.pathnode').addClass('pathnode-hidden');
    this.showNode(this.startId);

    // Load any lazy-loaded images/videos in the start node
    const $startNode = this.$container.find(`.pathnode[data-id="${this.startId}"]`);
    this.loadLazyContent($startNode);

    // Auto-play videos on the start node
    this.autoplayVideos($startNode);

    // Bind event handlers
    this.bindEvents();

    // Hide Start Over on the first node
    this.updateRestartButton();

    // Announce the initial node to screen readers
    this.announceNavigation(this.$container.find(`.pathnode[data-id="${this.startId}"]`));
  }

  bindEvents() {
    const self = this;

    // Next button click
    this.$container.on('click', '.pathnode-next', function(e) {
      e.preventDefault();
      const $currentNode = $(this).closest('.pathnode');
      const nodeType = $currentNode.data('type');

      // For question nodes, use the selected option's nextId
      let nextId;
      if (nodeType === 'question') {
        nextId = $currentNode.data('selected-nextid');
      } else {
        nextId = $currentNode.data('nextid');
      }

      if (nextId) {
        self.navigateTo(nextId);
      }
    });

    // Option button click (for question nodes) - select option and enable Next
    this.$container.on('click', '.pathoption', function(e) {
      e.preventDefault();
      const $option = $(this);
      const nextId = $option.data('nextid');
      const $node = $option.closest('.pathnode');

      if (nextId) {
        // Visual and accessible feedback for selected option
        $option.siblings('.pathoption').attr('aria-pressed', 'false').removeClass('selected');
        $option.attr('aria-pressed', 'true').addClass('selected');

        // Store selection and enable the Next button
        $node.data('selected-nextid', nextId);
        $node.find('.pathnode-next')
          .prop('disabled', false)
          .removeAttr('title');
      }
    });

    // Back button click
    this.$container.on('click', '.pathnode-back', function(e) {
      e.preventDefault();
      self.goBack();
    });

    // Restart button click
    this.$container.on('click', '.pathnode-restart', function(e) {
      e.preventDefault();
      self.restart();
    });

  }

  navigateTo(nodeId) {
    if (!nodeId || this.isTransitioning) return;
    this.isTransitioning = true;

    const $currentNode = this.$container.find(`.pathnode[data-id="${this.currentNodeId}"]`);
    const $nextNode = this.$container.find(`.pathnode[data-id="${nodeId}"]`);

    if ($nextNode.length === 0) {
      console.warn(`InteractivePath: Node with id "${nodeId}" not found`);
      this.isTransitioning = false;
      return;
    }

    // Save current position to history
    this.history.push(this.currentNodeId);

    // Animate transition
    this.transitionNodes($currentNode, $nextNode);

    // Update current node
    this.currentNodeId = nodeId;

    // Show back button now that we have history
    this.updateBackButton();
    this.updateRestartButton();

    // Announce to screen readers
    this.announceNavigation($nextNode);
  }

  transitionNodes($fromNode, $toNode) {
    const self = this;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationDuration = prefersReducedMotion ? 0 : 300;

    // Pause any playing videos on the node we're leaving
    this.pauseVideos($fromNode);

    // Slide out current node
    $fromNode.animate(
      { opacity: 0 },
      animationDuration,
      function() {
        $fromNode.addClass('pathnode-hidden');

        // Prepare next node
        $toNode.css({ opacity: 0 }).removeClass('pathnode-hidden');

        // Load any lazy-loaded images/videos in the new node
        self.loadLazyContent($toNode);

        // Scroll to top of the interactive path container
        self.scrollToTop();

        // Slide in next node
        $toNode.animate(
          { opacity: 1 },
          animationDuration,
          function() {
            // Focus on the new node for accessibility
            $toNode.find('.pathtitle').focus();
            self.isTransitioning = false;

            // Auto-play videos after fade-in completes
            self.autoplayVideos($toNode);
          }
        );
      }
    );
  }

  showNode(nodeId) {
    const $node = this.$container.find(`.pathnode[data-id="${nodeId}"]`);
    if ($node.length) {
      $node.css({ opacity: 1 }).removeClass('pathnode-hidden');
    }
  }

  loadLazyContent($node) {
    $node.find('.lazy-content').each(function () {
      var elem = $(this);
      elem.removeClass('lazy-content').addClass('lazy-content-loaded');
      var vidElement = $(window.Base64.decode(elem.attr('data-encoded-html') || ''));
      elem.append(vidElement);

      if (elem.attr('data-encoded-html-playbutton')) {
        var playElement = $(window.Base64.decode(elem.attr('data-encoded-html-playbutton') || ''));
        elem.append(playElement);
        elem.click(function () {
          $(this).children('img').remove();
          var newElement = $(window.Base64.decode(elem.attr('data-encoded-html-player') || ''));
          elem.append(newElement);
        });
      }
      $(document).trigger('lazy-content-load', [vidElement]);
    });
  }

  autoplayVideos($node) {
    // Skip autoplay if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    $node.find('iframe[data-video-player="vimeo"][data-autoplay="true"]').each(function() {
      try {
        const player = new Player(this);
        player.ready().then(() => player.play()).catch(() => {});
      } catch (e) {
        // Browser may block autoplay — fail silently
      }
    });
  }

  pauseVideos($node) {
    $node.find('iframe[data-video-player="vimeo"]').each(function() {
      try {
        const player = new Player(this);
        player.pause().catch(() => {});
      } catch (e) {
        // Fail silently
      }
    });
  }

  scrollToTop() {
    // Scroll to the interactive path container
    const containerTop = this.$container.offset().top;
    const headerHeight = $('#header_navigation_container').outerHeight() || 0;
    const targetScroll = containerTop - headerHeight - 20;

    // Check if we're in the reader content area
    const $readerContent = $('#reader_content');
    if ($readerContent.length) {
      const scrollTarget = this.$container.offset().top
        - $readerContent.offset().top
        + $readerContent.scrollTop();
      $readerContent.animate({ scrollTop: scrollTarget - 20 }, 200);
    } else {
      $('html, body').animate({ scrollTop: targetScroll }, 200);
    }
  }

  restart() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const $currentNode = this.$container.find(`.pathnode[data-id="${this.currentNodeId}"]`);
    const $startNode = this.$container.find(`.pathnode[data-id="${this.startId}"]`);

    // Clear history
    this.history = [];

    // Clear any selected options
    this.$container.find('.pathoption.selected').removeClass('selected').attr('aria-pressed', 'false');

    // Re-disable Next buttons on question nodes and restore tooltip
    this.$container.find('.pathnode[data-type="question"]').each(function() {
      $(this).removeData('selected-nextid');
      $(this).find('.pathnode-next')
        .prop('disabled', true)
        .attr('title', 'Select an answer to continue');
    });

    // Transition back to start
    this.transitionNodes($currentNode, $startNode);

    // Update current node
    this.currentNodeId = this.startId;

    // Hide back button since history is cleared
    this.updateBackButton();
    this.updateRestartButton();

    // Announce to screen readers
    this.announceNavigation($startNode);
  }

  announceNavigation($node) {
    // Create or update aria-live region for screen reader announcements
    let $announcer = this.$container.find('.sr-announcer');
    if ($announcer.length === 0) {
      $announcer = $('<div class="sr-announcer sr-only" aria-live="polite" aria-atomic="true"></div>');
      this.$container.append($announcer);
    }

    const title = $node.find('.pathtitle').text().trim();
    const nodeType = $node.data('type');

    let announcement = `Navigated to: ${title}`;
    if (nodeType === 'terminal') {
      announcement += '. This is the final step.';
    } else if (nodeType === 'question') {
      announcement += '. Please choose an option to continue.';
    }

    $announcer.text(announcement);
  }

  goBack() {
    if (this.history.length === 0 || this.isTransitioning) return;
    this.isTransitioning = true;

    // Get the previous node from history
    const previousNodeId = this.history.pop();

    const $currentNode = this.$container.find(`.pathnode[data-id="${this.currentNodeId}"]`);
    const $previousNode = this.$container.find(`.pathnode[data-id="${previousNodeId}"]`);

    if ($previousNode.length === 0) {
      console.warn(`InteractivePath: Previous node with id "${previousNodeId}" not found`);
      this.isTransitioning = false;
      return;
    }

    // Clear selected options on both nodes — intentionally resets the previous
    // node's selection so the user must re-engage with the question when revisiting
    $currentNode.find('.pathoption.selected').removeClass('selected').attr('aria-pressed', 'false');
    $previousNode.find('.pathoption.selected').removeClass('selected').attr('aria-pressed', 'false');

    // Re-disable Next buttons on question nodes being left or returned to
    if ($currentNode.attr('data-type') === 'question') {
      $currentNode.removeData('selected-nextid');
      $currentNode.find('.pathnode-next')
        .prop('disabled', true)
        .attr('title', 'Select an answer to continue');
    }
    if ($previousNode.attr('data-type') === 'question') {
      $previousNode.removeData('selected-nextid');
      $previousNode.find('.pathnode-next')
        .prop('disabled', true)
        .attr('title', 'Select an answer to continue');
    }

    // Transition back
    this.transitionNodes($currentNode, $previousNode);

    // Update current node
    this.currentNodeId = previousNodeId;

    // Update back button visibility
    this.updateBackButton();
    this.updateRestartButton();

    // Announce to screen readers
    this.announceNavigation($previousNode);
  }

  updateBackButton() {
    const $backButtons = this.$container.find('.pathnode-back');
    const $currentNode = this.$container.find(`.pathnode[data-id="${this.currentNodeId}"]`);
    const isTerminal = $currentNode.data('type') === 'terminal';

    // Hide back button if no history OR if on a terminal node (only option is Start Over)
    if (this.history.length > 0 && !isTerminal) {
      $backButtons.show();
    } else {
      $backButtons.hide();
    }
  }

  updateRestartButton() {
    const $restartButtons = this.$container.find('.pathnode-restart');
    // Only show Start Over once the user has moved past the first node
    if (this.currentNodeId === this.startId && this.history.length === 0) {
      $restartButtons.hide();
    } else {
      $restartButtons.show();
    }
  }

  setFixedHeight() {
    const $nodes = this.$container.find('.pathnode');

    // Temporarily make all nodes visible and measurable without causing layout shift
    $nodes.each(function() {
      const $node = $(this);
      $node.data('was-hidden', $node.hasClass('pathnode-hidden'));
      $node.removeClass('pathnode-hidden').css({
        visibility: 'hidden',
        position: 'absolute',
        width: '100%',
      });
    });

    // Find the tallest node
    let maxHeight = 0;
    $nodes.each(function() {
      maxHeight = Math.max(maxHeight, $(this).outerHeight());
    });

    // Restore original visibility states
    $nodes.each(function() {
      const $node = $(this);
      $node.css({
        visibility: '',
        position: '',
        width: '',
      });
      if ($node.data('was-hidden')) {
        $node.addClass('pathnode-hidden');
      }
    });

    // Apply min-height to each pathnode so the container stays consistent
    if (maxHeight > 0) {
      $nodes.css('min-height', maxHeight + 'px');
    }
  }

  bindResizeHandler() {
    const self = this;
    let resizeTimer = null;

    this._resizeHandler = function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        // Clear existing min-height before recalculating
        self.$container.find('.pathnode').css('min-height', '');
        self.setFixedHeight();
      }, 250);
    };

    $(window).on('resize', this._resizeHandler);
  }

  destroy() {
    if (this._resizeHandler) {
      $(window).off('resize', this._resizeHandler);
    }
  }
}

/**
 * Initialize interactive paths on the page
 * @param {string} selector - jQuery selector for the container(s)
 */
export function initInteractivePath(selector) {
  $(selector).each(function() {
    // Don't initialize twice
    if ($(this).data('interactive-path-initialized')) return;

    const instance = new InteractivePath(this);
    $(this).data('interactive-path-instance', instance);
    $(this).data('interactive-path-initialized', true);
  });
}

export { InteractivePath };
